import {
    EventName,
    GoalEvent,
    MatchEvent,
    MatchLocation,
    SelectedTeam,
} from "refsix-js-models";
import { max, orderBy } from "lodash";

type EventMap = { [key: string]: MatchEvent };

// The side the user's team plays as
export const sideForLocation = (location: MatchLocation): SelectedTeam =>
    location === MatchLocation.away ? SelectedTeam.away : SelectedTeam.home;

// A goal counts for the side that scored it, and an own goal for the other side
const countsForSide = (event: MatchEvent, side: SelectedTeam): boolean => {
    if (event.eventName !== EventName.goal) return false;
    const goal = event as GoalEvent;
    return goal.ownGoal ? goal.team?.side !== side : goal.team?.side === side;
};

export const countGoalsForSide = (
    events: EventMap | undefined,
    side: SelectedTeam
): number =>
    Object.values(events || {}).filter((event) => countsForSide(event, side))
        .length;

/**
 * Makes a side's goal events add up to `count`: the newest goals go first when
 * the score drops, and new goals are added after every existing event. Returns
 * a new map; events are not mutated.
 */
export const setGoalsForSide = (
    events: EventMap,
    side: SelectedTeam,
    count: number,
    newGoal: (timestamp: number) => GoalEvent
): EventMap => {
    const result: EventMap = { ...events };
    const newestFirst = orderBy(
        Object.entries(events).filter(([, event]) =>
            countsForSide(event, side)
        ),
        ([, event]) => event.timestamp,
        "desc"
    );
    newestFirst
        .slice(0, Math.max(newestFirst.length - count, 0))
        .forEach(([key]) => delete result[key]);

    // Clear both keys and timestamps: they can differ, and a clash would overwrite an event
    const taken = [
        ...Object.keys(events).map(Number),
        ...Object.values(events).map((event) => event.timestamp),
    ];
    let nextTs = (max(taken.filter((n) => !isNaN(n))) || 0) + 1;
    for (let i = newestFirst.length; i < count; i++) {
        const goal = newGoal(nextTs++);
        result[String(goal.timestamp)] = goal;
    }
    return result;
};
